import {
  BillingCycleInterval,
  BundlePricing,
  FeatureCode,
  BundleItemType,
  BundleStatus,
  CheckoutPageType,
  CouponProductType,
  InAppPurchase,
  Media,
} from 'generated/graphql';
import {
  ProgramReadiness,
  ProgramStateKind,
} from '@client/new/pages/programs/model/programs.types';

/**
 * Derives the four-state `ProgramStateKind` from the wire flags carried by
 * `AllPrograms`. Used by bundle/offer surfaces to drive the status pill
 * (Programs list shares the same component).
 */
export const toProgramStateKind = (p: {
  isPublished: boolean;
  isArchived?: boolean;
}): ProgramStateKind => {
  if (p.isArchived) return 'archived';
  if (p.isPublished) return 'published';
  return 'draft';
};

export type RawContentItem = {
  type: string;
  value: {
    id: string;
    title: string;
    isArchived?: boolean;
    isPublished?: boolean;
    url?: string;
  };
};

export type RawPrograms = Array<{
  id: number;
  isVisible?: boolean | null;
  name: string | null;
  type: number;
  accessType: string;
  programBusinessModel?: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
  /**
   * Optional in the wire response — present once the backend ships the
   * `Program.readiness` field on `Collection` and the GraphQL fragments
   * here select it. Used by U9 to disable not-ready programs in the
   * Offer selection dropdown (sc-111866 / R11).
   */
  readiness?: ProgramReadiness | null;
}>;

export type RawSubscriptionTier = {
  id: number;
  name: string;
  prices?: Array<{
    billingCycleInterval: BillingCycleInterval;
    billingCycleIntervalUnit: number;
    price: number;
    isFreeTrialEnabled: boolean;
    providerPriceId?: string;
  }>;
};

export type RawSubscriptionBundle = {
  id: number;
  title: string;
  influencerId: number;
  subscriptionTierId?: number | null;
  isPlatformBundle: boolean;
  items?: Array<{
    itemId?: number;
    contentId?: string;
    itemType: string;
  }>;
  subscriptionTier: RawSubscriptionTier;
  pricing: BundlePricing;
  status: BundleStatus;
  description?: string;
  pushNotificationMessage?: string;
  pushNotificationSentAt?: string;
  pushNotificationUsageCount: number;
  upgradeDeeplinkUrl?: string | null;
};

export type RawOtpBundle = {
  id: number;
  title: string;
  influencerId: number;
  items?: Array<{
    itemId?: number;
    contentId?: string;
    itemType: string;
  }>;
  oneTimeProduct?: {
    price: number;
    id: number;
    playbookFee: number;
    providerPriceId: string;
    providerProductId: string;
  };
  pricing: BundlePricing;
  status: BundleStatus;
  description?: string;
  wasPurchased: boolean;
  checkoutPage: {
    url: string;
    type: CheckoutPageType;
  };
  landingPageUrl: string;
  deeplinkUrl: string;
  isFree: boolean;
  inAppPurchase: InAppPurchase;
  migrationProgramId: number | null;
  coverImage: Media | null;
};

export type RawFeatures = Array<{
  id: number;
  name: string;
  code: FeatureCode;
}>;

export type AllFeatures = Array<{
  id: number;
  name: string;
  code: FeatureCode;
}>;

export type AllContent = Array<{
  id: string;
  name: string;
  isPublished: boolean;
}>;

export type AllPrograms = Array<{
  id: number;
  name: string;
  isPublished: boolean;
  isArchived?: boolean;
  /**
   * Per-criterion publication readiness. `null` (or absent) means the
   * backend has not yet computed it (pre-backfill program) — the dropdown
   * treats such programs as ENABLED so the user is never punished for
   * deploy-window timing. Used by U9 to disable not-ready programs in
   * the selection dropdown.
   */
  readiness?: ProgramReadiness | null;
}>;

export type SubscriptionBundlePrice = {
  monthly?: number;
  annual?: number;
};

export type SubscriptionProviderPriceIds = {
  monthly?: string;
  annual?: string;
};

export type BundleFreeTrial = {
  monthly: boolean;
  annual: boolean;
};

export type BasicBundle = {
  id: number;
  title: string;
  influencerId: number;
  isPlatformBundle: boolean;
  items: Array<{
    contentId?: string;
    itemId?: number;
    itemType: BundleItemType;
  }>;
  pricing: BundlePricing;
  status: BundleStatus;
  description?: string;
};

export type SubscriptionBundle = BasicBundle & {
  price: SubscriptionBundlePrice;
  providerPriceId?: SubscriptionProviderPriceIds;
  freeTrial: BundleFreeTrial;
  pushNotificationMessage?: string | null;
  pushNotificationSentAt?: string | null;
  pushNotificationUsageCount: number;
  upgradeDeeplinkUrl?: string | null;
};

export type Features = Array<{
  id: number;
  name: string;
  code: FeatureCode;
}>;

export type Discount = {
  code: string;
  id: string;
  isActive: boolean;
  name: string;
  paidProductIds?: Array<number> | null;
  subscriptionProviderPriceId?: string | null;
  productType: CouponProductType;
};
